import type { DateTimeValue } from '../../../source/shared/date-types.js';
import type * as Core from '../../types/calculation-core.js';
import type { CivilDate, CalendarConfig, DurationConfig, DateAdjustmentConfig, AgeInput, DateDifferenceInput, DurationInput, DateAdjustmentInput } from './contracts.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
const ISO_DATE_SOURCE = {
    id: 'iso-8601-date-time-format',
    title: 'ISO 8601 — Date and time format',
    publisher: 'International Organization for Standardization',
    url: 'https://www.iso.org/iso-8601-date-and-time-format.html',
    verifiedAt: '2026-08-20',
};
const GREGORIAN_SOURCE = {
    id: 'usno-leap-years',
    title: 'Leap Years',
    publisher: 'U.S. Naval Observatory',
    url: 'https://aa.usno.navy.mil/faq/leap_years',
    verifiedAt: '2026-08-20',
};
const SI_TIME_SOURCE = {
    id: 'bipm-si-brochure',
    title: 'The International System of Units (SI), 9th edition',
    publisher: 'Bureau International des Poids et Mesures',
    url: 'https://www.bipm.org/en/publications/si-brochure',
    verifiedAt: '2026-08-20',
};
function calendarMetadata(calculatorId: string, note: string): Core.CoreMetadata {
    return {
        calculatorId,
        version: '1.0.0',
        effectiveFrom: '2026-08-20',
        lastVerifiedAt: '2026-08-20',
        sources: [ISO_DATE_SOURCE, GREGORIAN_SOURCE],
        notes: [note],
    };
}
export const DEFAULT_AGE_CONFIG = {
    metadata: calendarMetadata('tarih-zaman.yas-hesaplama', 'Tarih-only girdiler UTC takvim günü semantiğindedir. Çekirdek sistem saatini okumaz; dinamik “bugün” değeri çağıran tarafından referenceDate olarak verilir. 29 Şubat yıldönümü artık olmayan yıllarda 28 Şubat kabul edilir.'),
    minYear: 1,
    maxYear: 9999,
};
export const DEFAULT_DATE_DIFFERENCE_CONFIG = {
    metadata: calendarMetadata('tarih-zaman.iki-tarih-arasi-fark-hesaplama', 'Tarih-only girdiler proleptik Gregoryen UTC takvim günleri olarak değerlendirilir; yerel saat ve yaz/kış saati farkı hesaba katılmaz.'),
    minYear: 1,
    maxYear: 9999,
};
export const DEFAULT_DURATION_CONFIG = {
    metadata: {
        calculatorId: 'tarih-zaman.saat-sure-hesaplama',
        version: '1.0.0',
        effectiveFrom: '2026-08-20',
        lastVerifiedAt: '2026-08-20',
        sources: [SI_TIME_SOURCE],
        notes: ['Saat ve dakika toplama/çıkarma saf aritmetiktir; girilen dakika değeri 59’u aşarsa saat hanesine normalize edilir.'],
    },
    maxHours: 99999,
    maxMinutes: 9999,
};
export const DEFAULT_DATE_ADJUSTMENT_CONFIG = {
    metadata: calendarMetadata('tarih-zaman.tarihe-gun-ekleme-cikarma-hesaplama', 'Tarih-only girdiler UTC takvim günü semantiğindedir. Yıl ve ay önce uygulanır; hedef ay daha kısaysa gün ayın son gününe sıkıştırılır. Hafta ve gün daha sonra takvim günü olarak eklenir.'),
    minYear: 1,
    maxYear: 9999,
    maxYears: 100,
    maxMonths: 1200,
    maxWeeks: 9999,
    maxDays: 999999,
};
export const DEFAULT_ISO_WEEK_CONFIG = {
    metadata: calendarMetadata('tarih-zaman.hafta-hesaplama', 'ISO hafta Pazartesi başlar; 1. hafta yılın ilk Perşembesini içerir. ISO hafta yılı takvim yılından farklı olabilir.'),
    minYear: 1,
    maxYear: 9999,
};
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
function isLeapYear(year: number) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
function daysInMonth(year: number, month: number) {
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
function parseIsoDate(value: unknown): CivilDate | null {
    if (typeof value !== 'string')
        return null;
    const match = ISO_DATE_PATTERN.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isInteger(year) || year < 1 || month < 1 || month > 12)
        return null;
    if (day < 1 || day > daysInMonth(year, month))
        return null;
    return { year, month, day };
}
function formatIsoDate(date: CivilDate) {
    return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}
function daysFromCivil(date: CivilDate) {
    let year = date.year;
    year -= date.month <= 2 ? 1 : 0;
    const era = Math.floor(year / 400);
    const yearOfEra = year - era * 400;
    const shiftedMonth = date.month + (date.month > 2 ? -3 : 9);
    const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + date.day - 1;
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146097 + dayOfEra - 719468;
}
function civilFromDays(serialDay: number): CivilDate {
    const shifted = serialDay + 719468;
    const era = Math.floor(shifted / 146097);
    const dayOfEra = shifted - era * 146097;
    const yearOfEra = Math.floor((dayOfEra - Math.floor(dayOfEra / 1460) + Math.floor(dayOfEra / 36524) - Math.floor(dayOfEra / 146096)) / 365);
    let year = yearOfEra + era * 400;
    const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
    const monthPrime = Math.floor((5 * dayOfYear + 2) / 153);
    const day = dayOfYear - Math.floor((153 * monthPrime + 2) / 5) + 1;
    const month = monthPrime + (monthPrime < 10 ? 3 : -9);
    year += month <= 2 ? 1 : 0;
    return { year, month, day };
}
function compareCivil(left: CivilDate, right: CivilDate) {
    return daysFromCivil(left) - daysFromCivil(right);
}
function addDays(date: CivilDate, days: number) {
    return civilFromDays(daysFromCivil(date) + days);
}
function addMonthsClamped(date: CivilDate, months: number) {
    const targetMonthIndex = date.year * 12 + (date.month - 1) + months;
    const year = Math.floor(targetMonthIndex / 12);
    const month = targetMonthIndex - year * 12 + 1;
    return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}
function anniversary(date: CivilDate, year: number) {
    return { year, month: date.month, day: Math.min(date.day, daysInMonth(year, date.month)) };
}
function positiveModulo(value: number, divisor: number) {
    return ((value % divisor) + divisor) % divisor;
}
function isoWeekday(date: CivilDate) {
    return positiveModulo(daysFromCivil(date) + 3, 7) + 1;
}
function dayOfYear(date: CivilDate) {
    return daysFromCivil(date) - daysFromCivil({ year: date.year, month: 1, day: 1 }) + 1;
}
function calendarDifference(start: CivilDate, end: CivilDate) {
    let years = end.year - start.year;
    let cursor = anniversary(start, start.year + years);
    if (compareCivil(cursor, end) > 0) {
        years -= 1;
        cursor = anniversary(start, start.year + years);
    }
    let months = Math.min(11, (end.year - cursor.year) * 12 + end.month - cursor.month);
    let monthCursor = addMonthsClamped(cursor, months);
    if (compareCivil(monthCursor, end) > 0) {
        months -= 1;
        monthCursor = addMonthsClamped(cursor, months);
    }
    const days = daysFromCivil(end) - daysFromCivil(monthCursor);
    return { years, months, days };
}
function metadataErrors(metadata: Core.CoreMetadata, expectedCalculatorId: string): Core.InputError[] {
    const errors = [...validateMetadata(metadata)];
    if (metadata?.calculatorId !== expectedCalculatorId) {
        errors.push({
            field: 'config.metadata.calculatorId',
            code: 'INVALID_CONFIG',
            message: `Kural metadata kimliği ${expectedCalculatorId} olmalıdır.`,
        });
    }
    return errors;
}
function validateCalendarConfig(config: CalendarConfig, expectedCalculatorId: string): Core.InputError[] {
    const errors = metadataErrors(config.metadata, expectedCalculatorId);
    for (const field of ['minYear', 'maxYear'] as const) {
        const value = config[field];
        if (!isFiniteNumber(value) || !Number.isInteger(value) || value < 1 || value > 9999) {
            errors.push({ field: `config.${field}`, code: 'INVALID_CONFIG', message: `${field} 1-9999 arasında bir tamsayı olmalıdır.` });
        }
    }
    if (Number.isInteger(config.minYear) && Number.isInteger(config.maxYear) && config.minYear > config.maxYear) {
        errors.push({ field: 'config.maxYear', code: 'INVALID_CONFIG', message: 'maxYear, minYear değerinden küçük olamaz.' });
    }
    return errors;
}
function validateDateAdjustmentConfig(config: DateAdjustmentConfig): Core.InputError[] {
    const errors = [...validateCalendarConfig(config, 'tarih-zaman.tarihe-gun-ekleme-cikarma-hesaplama')];
    for (const field of ['maxYears', 'maxMonths', 'maxWeeks', 'maxDays'] as const) {
        const value = config[field];
        if (!isFiniteNumber(value) || !Number.isSafeInteger(value) || value < 0) {
            errors.push({ field: `config.${field}`, code: 'INVALID_CONFIG', message: `${field} negatif olmayan güvenli bir tamsayı olmalıdır.` });
        }
    }
    if (Number.isSafeInteger(config.maxYears)
        && Number.isSafeInteger(config.maxMonths)
        && config.maxYears * 12 + config.maxMonths > Number.MAX_SAFE_INTEGER) {
        errors.push({ field: 'config.maxMonths', code: 'INVALID_CONFIG', message: 'Yıl ve ay üst sınırlarının toplamı güvenli sayı aralığını aşamaz.' });
    }
    if (Number.isSafeInteger(config.maxWeeks)
        && Number.isSafeInteger(config.maxDays)
        && config.maxWeeks * 7 + config.maxDays > Number.MAX_SAFE_INTEGER) {
        errors.push({ field: 'config.maxDays', code: 'INVALID_CONFIG', message: 'Hafta ve gün üst sınırlarının toplamı güvenli sayı aralığını aşamaz.' });
    }
    return errors;
}
function validateDurationConfig(config: DurationConfig): Core.InputError[] {
    const errors = metadataErrors(config.metadata, 'tarih-zaman.saat-sure-hesaplama');
    for (const field of ['maxHours', 'maxMinutes'] as const) {
        const value = config[field];
        if (!isFiniteNumber(value) || !Number.isSafeInteger(value) || value < 0) {
            errors.push({ field: `config.${field}`, code: 'INVALID_CONFIG', message: `${field} negatif olmayan güvenli bir tamsayı olmalıdır.` });
        }
    }
    if (Number.isSafeInteger(config.maxHours)
        && Number.isSafeInteger(config.maxMinutes)
        && (config.maxHours * 60 + config.maxMinutes) * 2 > Number.MAX_SAFE_INTEGER) {
        errors.push({ field: 'config.maxMinutes', code: 'INVALID_CONFIG', message: 'Süre üst sınırları toplama işleminde güvenli sayı aralığını aşamaz.' });
    }
    return errors;
}
function dateErrors(value: unknown, field: string, config: CalendarConfig) {
    if (typeof value === 'number' && !Number.isFinite(value)) {
        return {
            date: null,
            errors: [{ field, code: 'NOT_FINITE', message: `${field} sonlu bir tarih değeri olmalıdır.` }],
        };
    }
    if (value === null || value === undefined || value === '') {
        return {
            date: null,
            errors: [{ field, code: 'REQUIRED', message: `${field} tarihi gereklidir.` }],
        };
    }
    if (typeof value !== 'string' || value.trim() === '') {
        return {
            date: null,
            errors: [{ field, code: 'UNSUPPORTED_VALUE', message: `${field} geçerli YYYY-AA-GG biçiminde olmalıdır.` }],
        };
    }
    const date = parseIsoDate(value);
    if (!date) {
        return {
            date: null,
            errors: [{ field, code: 'UNSUPPORTED_VALUE', message: `${field} geçerli YYYY-AA-GG biçiminde olmalıdır.` }],
        };
    }
    if (date.year < config.minYear || date.year > config.maxYear) {
        return {
            date: null,
            errors: [{ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen yıl aralığının dışındadır.` }],
        };
    }
    return { date, errors: [] };
}
function configFailureMetadata(candidate: Core.CoreMetadata | undefined, fallback: Core.CoreMetadata) {
    const resolved = resolveMetadata(candidate, fallback);
    return resolved.calculatorId === fallback.calculatorId ? resolved : fallback;
}
export function calculateAge(input: Readonly<Record<string, unknown>>, config: CalendarConfig = DEFAULT_AGE_CONFIG): Core.Calculation<DateTimeValue> {
    const metadata = configFailureMetadata(config?.metadata, DEFAULT_AGE_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateCalendarConfig(config, 'tarih-zaman.yas-hesaplama')
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Yaş konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Yaş hesabı girdileri gereklidir.' }], metadata);
    }
    const birthResult = dateErrors(input.birthDate, 'birthDate', config);
    const referenceResult = dateErrors(input.referenceDate, 'referenceDate', config);
    const errors = [...birthResult.errors, ...referenceResult.errors];
    if (birthResult.date && referenceResult.date && compareCivil(referenceResult.date, birthResult.date) < 0) {
        errors.push({ field: 'referenceDate', code: 'OUT_OF_RANGE', message: 'Hedef tarih doğum tarihinden önce olamaz.' });
    }
    if (errors.length > 0 || !birthResult.date || !referenceResult.date)
        return failure(errors, metadata);
    const birthDate = birthResult.date;
    const referenceDate = referenceResult.date;
    const detail = calendarDifference(birthDate, referenceDate);
    const totalDays = daysFromCivil(referenceDate) - daysFromCivil(birthDate);
    let nextBirthdayDate = anniversary(birthDate, referenceDate.year);
    if (compareCivil(nextBirthdayDate, referenceDate) <= 0) {
        nextBirthdayDate = anniversary(birthDate, referenceDate.year + 1);
    }
    const nextBirthdayInRange = nextBirthdayDate.year <= config.maxYear;
    const nextBirthday = nextBirthdayInRange ? formatIsoDate(nextBirthdayDate) : null;
    const daysUntilNextBirthday = nextBirthdayInRange
        ? daysFromCivil(nextBirthdayDate) - daysFromCivil(referenceDate)
        : null;
    return success({
        birthDate: input.birthDate,
        referenceDate: input.referenceDate,
        ...detail,
        totalDays,
        totalHours: totalDays * 24,
        totalMinutes: totalDays * 24 * 60,
        nextBirthday,
        daysUntilNextBirthday,
    }, metadata);
}
export function calculateDateDifference(input: Readonly<Record<string, unknown>>, config: CalendarConfig = DEFAULT_DATE_DIFFERENCE_CONFIG): Core.Calculation<DateTimeValue> {
    const metadata = configFailureMetadata(config?.metadata, DEFAULT_DATE_DIFFERENCE_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateCalendarConfig(config, 'tarih-zaman.iki-tarih-arasi-fark-hesaplama')
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Tarih farkı konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Tarih farkı girdileri gereklidir.' }], metadata);
    }
    const startResult = dateErrors(input.startDate, 'startDate', config);
    const endResult = dateErrors(input.endDate, 'endDate', config);
    const errors = [...startResult.errors, ...endResult.errors];
    if (startResult.date && endResult.date && compareCivil(endResult.date, startResult.date) < 0) {
        errors.push({ field: 'endDate', code: 'OUT_OF_RANGE', message: 'Bitiş tarihi başlangıç tarihinden önce olamaz.' });
    }
    if (errors.length > 0 || !startResult.date || !endResult.date)
        return failure(errors, metadata);
    const detail = calendarDifference(startResult.date, endResult.date);
    const totalDays = daysFromCivil(endResult.date) - daysFromCivil(startResult.date);
    return success({
        ...input,
        ...detail,
        totalDays,
        totalWeeks: totalDays / 7,
        totalHours: totalDays * 24,
        totalMinutes: totalDays * 24 * 60,
    }, metadata);
}
function normalizeDuration(hours: number, minutes: number) {
    const totalMinutes = hours * 60 + minutes;
    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
        totalMinutes,
    };
}
export function calculateDuration(input: Readonly<Record<string, unknown>>, config: DurationConfig = DEFAULT_DURATION_CONFIG): Core.Calculation<DateTimeValue> {
    const metadata = configFailureMetadata(config?.metadata, DEFAULT_DURATION_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateDurationConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Süre konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Süre hesabı girdileri gereklidir.' }], metadata);
    }
    const operation = input.operation;
    const errors = [];
    if (operation !== 'add' && operation !== 'subtract') {
        errors.push({ field: 'operation', code: 'UNSUPPORTED_VALUE', message: 'Süre işlem türü desteklenmiyor.' });
    }
    const values: Partial<Record<'firstHours' | 'secondHours' | 'firstMinutes' | 'secondMinutes', unknown>> = {};
    for (const field of ['firstHours', 'secondHours'] as const) {
        const value = input[field];
        values[field] = value;
        if (typeof value !== 'number' || !isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
        }
        else if (!Number.isSafeInteger(value) || value < 0 || value > config.maxHours) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen tamsayı aralığının dışındadır.` });
        }
    }
    for (const field of ['firstMinutes', 'secondMinutes'] as const) {
        const value = input[field];
        values[field] = value;
        if (typeof value !== 'number' || !isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
        }
        else if (!Number.isSafeInteger(value) || value < 0 || value > config.maxMinutes) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen tamsayı aralığının dışındadır.` });
        }
    }
    const { firstHours, firstMinutes, secondHours, secondMinutes } = values;
    if (errors.length > 0 || typeof firstHours !== 'number' || !isFiniteNumber(firstHours) || typeof firstMinutes !== 'number' || !isFiniteNumber(firstMinutes) || typeof secondHours !== 'number' || !isFiniteNumber(secondHours) || typeof secondMinutes !== 'number' || !isFiniteNumber(secondMinutes) || (operation !== 'add' && operation !== 'subtract'))
        return failure(errors, metadata);
    const first = normalizeDuration(firstHours, firstMinutes);
    const second = normalizeDuration(secondHours, secondMinutes);
    const rawTotal = operation === 'add'
        ? first.totalMinutes + second.totalMinutes
        : first.totalMinutes - second.totalMinutes;
    const signedTotalMinutes = Object.is(rawTotal, -0) ? 0 : rawTotal;
    const absoluteTotalMinutes = Math.abs(signedTotalMinutes);
    return success({
        operation: operation,
        first,
        second,
        hours: Math.floor(absoluteTotalMinutes / 60),
        minutes: absoluteTotalMinutes % 60,
        signedTotalMinutes,
        absoluteTotalMinutes,
        decimalHours: signedTotalMinutes / 60,
        isNegative: signedTotalMinutes < 0,
    }, metadata);
}
export function calculateDateAdjustment(input: Readonly<Record<string, unknown>>, config: DateAdjustmentConfig = DEFAULT_DATE_ADJUSTMENT_CONFIG): Core.Calculation<DateTimeValue> {
    const metadata = configFailureMetadata(config?.metadata, DEFAULT_DATE_ADJUSTMENT_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateDateAdjustmentConfig(config)
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'Tarih ekleme/çıkarma konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Tarih ekleme/çıkarma girdileri gereklidir.' }], metadata);
    }
    const startDate = input.startDate;
    const startResult = dateErrors(startDate, 'startDate', config);
    const operation = input.operation;
    const errors = [...startResult.errors];
    if (operation !== 'add' && operation !== 'subtract') {
        errors.push({ field: 'operation', code: 'UNSUPPORTED_VALUE', message: 'Tarih işlem türü desteklenmiyor.' });
    }
    const values: Partial<Record<'years' | 'months' | 'weeks' | 'days', unknown>> = {};
    const limits = {
        years: config.maxYears,
        months: config.maxMonths,
        weeks: config.maxWeeks,
        days: config.maxDays,
    };
    for (const field of ['years', 'months', 'weeks', 'days'] as const) {
        const value = input[field];
        values[field] = value;
        if (typeof value !== 'number' || !isFiniteNumber(value)) {
            errors.push({ field, code: 'NOT_FINITE', message: `${field} sonlu bir sayı olmalıdır.` });
        }
        else if (!Number.isSafeInteger(value) || value < 0 || value > limits[field]) {
            errors.push({ field, code: 'OUT_OF_RANGE', message: `${field} izin verilen tamsayı aralığının dışındadır.` });
        }
    }
    const { years, months, weeks, days } = values;
    if (errors.length > 0 || typeof years !== 'number' || !isFiniteNumber(years) || typeof months !== 'number' || !isFiniteNumber(months) || typeof weeks !== 'number' || !isFiniteNumber(weeks) || typeof days !== 'number' || !isFiniteNumber(days) || typeof startDate !== 'string' || (operation !== 'add' && operation !== 'subtract') || !startResult.date)
        return failure(errors, metadata);
    const direction = operation === 'add' ? 1 : -1;
    const monthDelta = direction * (years * 12 + months);
    const dayDelta = direction * (weeks * 7 + days);
    const monthAdjusted = addMonthsClamped(startResult.date, monthDelta);
    const resultDate = addDays(monthAdjusted, dayDelta);
    if (resultDate.year < config.minYear || resultDate.year > config.maxYear) {
        return failure([{
                field: 'resultDate',
                code: 'OUT_OF_RANGE',
                message: 'Hesaplanan tarih izin verilen yıl aralığının dışındadır.',
            }], metadata);
    }
    return success({
        ...input,
        resultDate: formatIsoDate(resultDate),
        totalDays: daysFromCivil(resultDate) - daysFromCivil(startResult.date),
        isoWeekday: isoWeekday(resultDate),
        dayOfYear: dayOfYear(resultDate),
    }, metadata);
}
function inCalendarRange(date: CivilDate, config: CalendarConfig) {
    return date.year >= config.minYear && date.year <= config.maxYear;
}
export function calculateIsoWeek(input: Readonly<Record<string, unknown>>, config: CalendarConfig = DEFAULT_ISO_WEEK_CONFIG): Core.Calculation<DateTimeValue> {
    const metadata = configFailureMetadata(config?.metadata, DEFAULT_ISO_WEEK_CONFIG.metadata);
    const configErrors = config && typeof config === 'object'
        ? validateCalendarConfig(config, 'tarih-zaman.hafta-hesaplama')
        : [{ field: 'config', code: 'INVALID_CONFIG', message: 'ISO hafta konfigürasyonu geçersiz.' }];
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    if (!input || typeof input !== 'object') {
        return failure([{ field: 'input', code: 'REQUIRED', message: 'ISO hafta girdisi gereklidir.' }], metadata);
    }
    const rawDate = input.date;
    const dateResult = dateErrors(rawDate, 'date', config);
    if (dateResult.errors.length > 0 || !dateResult.date || typeof rawDate !== 'string')
        return failure(dateResult.errors, metadata);
    const date = dateResult.date;
    const weekday = isoWeekday(date);
    const thursday = addDays(date, 4 - weekday);
    const isoYear = thursday.year;
    const januaryFourth = { year: isoYear, month: 1, day: 4 };
    const weekOneMonday = addDays(januaryFourth, -(isoWeekday(januaryFourth) - 1));
    const isoWeek = Math.floor((daysFromCivil(date) - daysFromCivil(weekOneMonday)) / 7) + 1;
    const weekStartDate = addDays(date, -(weekday - 1));
    const weekEndDate = addDays(weekStartDate, 6);
    return success({
        date: rawDate,
        isoWeek,
        isoYear,
        isoWeekday: weekday,
        dayOfYear: dayOfYear(date),
        weekStart: inCalendarRange(weekStartDate, config) ? formatIsoDate(weekStartDate) : null,
        weekEnd: inCalendarRange(weekEndDate, config) ? formatIsoDate(weekEndDate) : null,
    }, metadata);
}
